// src/data/blogSampleOffer.ts
//
// BLOG-LEAD-MAGNET-SAMPLES-001 — the ONE wording of the blog "free 15-page
// sample" offer. Read by src/components/feature/BlogSampleOffer.tsx.
//
// Honesty rules (asserted by scripts/check-blog-lead-magnet.mjs):
//   * Always "15-page sample". The complete planner / workbook is never offered
//     here — it is described only as included with ELIGIBLE PawTenant orders,
//     which is the entitlement that actually exists (src/data/plannerBenefit.ts).
//   * No approval, housing, training or certification promise.
//   * MARKETING_CONSENT.text is the exact label of the separate, UNCHECKED box
//     and must equal supabase/functions/_shared/leadMagnetSamples.ts — that is
//     the text stored with the consent record.

export type SampleVariant = "esa" | "psd";

export interface SampleOfferCopy {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  note: string;
  /** Short name used in the success message and the ESA/PSD choice. */
  shortName: string;
  /** One line under the option in the ESA/PSD choice. */
  choiceHint: string;
  image: { src: string; srcSet: string; alt: string };
}

export const SAMPLE_OFFER_COPY: Record<SampleVariant, SampleOfferCopy> = {
  esa: {
    eyebrow: "Free 15-page sample",
    title: "Get the free ESA Pet Care Planner sample",
    body: "Enter your email and we will send you a free 15-page sample of the PawTenant ESA Pet Care Planner as a printable PDF.",
    bullets: [
      "15 printable pages from the 33-page planner",
      "Vaccination, medication and vet records",
      "Daily care, training log and emergency information",
    ],
    note: "This is a sample. The complete planner is included with eligible PawTenant ESA letter orders.",
    shortName: "ESA Pet Care Planner sample",
    choiceHint: "Records, vet details and daily care for an emotional support animal",
    image: {
      src: "/assets/planner/pet-care-planner-cover-240.webp",
      srcSet: "/assets/planner/pet-care-planner-cover-240.webp 240w, /assets/planner/pet-care-planner-cover-480.webp 480w",
      alt: "Cover of the PawTenant Pet Care Planner",
    },
  },
  psd: {
    eyebrow: "Free 15-page sample",
    title: "Get the free PSD Training Workbook sample",
    body: "Enter your email and we will send you a free 15-page sample of the PawTenant Psychiatric Service Dog Training Workbook as a printable PDF.",
    bullets: [
      "15 printable pages from the 30-page workbook",
      "Task training framework and a worked example",
      "Public access session log and the optional 120-hour tracker",
    ],
    note: "This is a sample. It does not certify a dog. The complete workbook is included with eligible PawTenant PSD letter orders.",
    shortName: "PSD Training Workbook sample",
    choiceHint: "Task training, public access logs and records for a psychiatric service dog",
    image: {
      src: "/assets/planner/psd-workbook-cover-240.webp",
      srcSet: "/assets/planner/psd-workbook-cover-240.webp 240w, /assets/planner/psd-workbook-cover-480.webp 480w",
      alt: "Cover of the PawTenant Psychiatric Service Dog Training Workbook",
    },
  },
};

/** Copy for a genuinely mixed article: the visitor chooses, nothing is pre-selected. */
export const CHOICE_OFFER_COPY = {
  eyebrow: "Free 15-page sample",
  title: "Get a free 15-page sample PDF",
  body: "Choose the sample that fits you and we will email it to you as a printable PDF.",
  legend: "Which free sample would you like?",
  note: "These are samples. The complete editions are included with eligible PawTenant ESA or PSD letter orders.",
} as const;

/** The separate, unchecked, optional marketing box. Version bumps with any wording change. */
export const MARKETING_CONSENT = {
  version: "blog-tips-v1",
  text: "Also send me occasional PawTenant emails with ESA and PSD tips. Unsubscribe anytime.",
} as const;

export const SAMPLE_OFFER_FINE_PRINT =
  "One email with the PDF attached. No account or payment. We use your address only to send this sample, and to send tips only if you tick the box.";

export const SAMPLE_OFFER_ERRORS = {
  invalid_email: "Please enter a valid email address.",
  choose: "Please choose which free sample you would like.",
  rate_limited: "Too many requests from this connection. Please wait a few minutes and try again.",
  failed: "We could not send your sample just now. Please try again in a minute.",
} as const;
